"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface BookmarkIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface BookmarkIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  isBookmarked?: boolean;
  fillColor?: string;
  className?: string;
}

const BookmarkIcon = forwardRef<BookmarkIconHandle, BookmarkIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
      isBookmarked = false,
      fillColor = "#CCFF00",
    },
    ref,
  ) => {
    const controls = useAnimation();
    const sparkControls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
        sparkControls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
        sparkControls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) {
        controls.start("animate");
        sparkControls.start("animate");
      }
    }, [controls, sparkControls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) {
        controls.start("normal");
        sparkControls.start("normal");
      }
    }, [controls, sparkControls]);

    const bookmarkVariants: Variants = {
      normal: { scale: 1 },
      animate: {
        scale: [1, 1.15, 0.9, 1],
        transition: { duration: 1.2 * duration, repeat: 0, ease: "easeInOut" },
      },
    };

    const sparkVariants: Variants = {
      normal: { opacity: 0, scale: 0 },
      animate: {
        opacity: [0.8, 0, 0],
        scale: [1, 1.5, 0],
        transition: { duration: 1.2 * duration, repeat: 0, ease: "easeOut" },
      },
    };

    return (
      <motion.div
        className={`relative inline-flex ${className || ""}`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill={isBookmarked ? fillColor : "none"}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          variants={bookmarkVariants}
          animate={controls}
          initial="normal"
          style={isBookmarked ? { color: fillColor } : undefined}
        >
          <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
        </motion.svg>

        <motion.div
          className="absolute -top-1 right-0"
          animate={sparkControls}
          initial="normal"
        >
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size / 3}
            height={size / 3}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            variants={sparkVariants}
          >
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </motion.svg>
        </motion.div>
      </motion.div>
    );
  },
);

BookmarkIcon.displayName = "BookmarkIcon";
export { BookmarkIcon };
